/**
 * Soil Load Drawer Component
 * Drawer for configuring soil load levels standards
 * Enhanced with spring physics animation and dropdown select
 */

import React, { useState, useEffect } from 'react';
import { X, ChevronDown, TrendingUp, TrendingDown } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

const soilLoadOptions = [
  { value: 'super-heavy', label: 'Super Heavy Soil Load' },
  { value: 'heavy', label: 'Heavy Soil Load' },
  { value: 'medium', label: 'Medium Soil Load' },
  { value: 'light', label: 'Light Soil Load' },
  { value: 'super-light', label: 'Super Light Soil Load' },
];

export const SoilLoadDrawer = ({ isOpen, onClose, onSave, initialLevel = 'heavy' }) => {
  const [selectedLevel, setSelectedLevel] = useState(initialLevel);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // Update selected level when initialLevel changes
  useEffect(() => {
    setSelectedLevel(initialLevel);
  }, [initialLevel]);

  const handleSave = () => {
    onSave?.(selectedLevel);
    onClose();
  };

  const selectedOption = soilLoadOptions.find((opt) => opt.value === selectedLevel);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop with fade animation */}
          <motion.div
            className="fixed inset-0 bg-black/50 z-40"
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          />

          {/* Drawer with spring physics */}
          <motion.div
            className="fixed top-0 right-0 h-full w-full max-w-[640px] bg-white z-50 shadow-2xl"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{
              type: 'spring',
              damping: 25,
              stiffness: 200,
              mass: 0.8,
            }}
          >
            <div className="h-full flex flex-col">
              {/* Header */}
              <div className="flex items-center justify-between px-8 py-6 border-b border-gray-200">
                <h2 className="font-['Roboto:Bold',sans-serif] font-bold text-[24px] text-black">
                  Set Soil Load Standards
                </h2>
                <button
                  onClick={onClose}
                  className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
                  type="button"
                >
                  <X className="size-5 text-gray-600" />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto px-8 py-6">
                {/* Description */}
                <p className="font-['Roboto:Regular',sans-serif] text-[14px] text-gray-600 italic leading-relaxed mb-6">
                  Set the soil load levels for the facilities. Soil load affects cleaning frequency
                  and resource allocation across your organization.
                </p>

                {/* Dropdown Section */}
                <div className="mb-8">
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                      className="w-full bg-white border-2 border-gray-300 rounded-lg px-5 py-4 flex items-center justify-between hover:border-[#61b8e7] transition-colors"
                    >
                      <span className="font-['Roboto:Medium',sans-serif] text-[16px] text-black">
                        {selectedOption?.label}
                      </span>
                      <ChevronDown
                        className={`w-5 h-5 text-gray-500 transition-transform ${
                          isDropdownOpen ? 'rotate-180' : ''
                        }`}
                      />
                    </button>

                    {/* Dropdown Menu */}
                    <AnimatePresence>
                      {isDropdownOpen && (
                        <motion.div
                          className="absolute top-full left-0 right-0 mt-2 bg-white border-2 border-gray-200 rounded-lg shadow-lg z-10 overflow-hidden"
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          transition={{ duration: 0.15 }}
                        >
                          {soilLoadOptions.map((option) => (
                            <button
                              key={option.value}
                              type="button"
                              onClick={() => {
                                setSelectedLevel(option.value);
                                setIsDropdownOpen(false);
                              }}
                              className={`
                                w-full px-5 py-3.5 text-left transition-colors
                                ${
                                  selectedLevel === option.value
                                    ? 'bg-[#61b8e7]/10 text-[#61b8e7]'
                                    : 'hover:bg-gray-50 text-gray-700'
                                }
                              `}
                            >
                              <span className="font-['Roboto:Medium',sans-serif] text-[15px]">
                                {option.label}
                              </span>
                            </button>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>

                {/* Reminder Section */}
                <div className="bg-[#f8fbfd] border border-[#e5e5e5] rounded-lg p-5">
                  <p className="font-['Roboto:Regular',sans-serif] text-[14px] text-gray-600 italic mb-3">
                    Reminder:
                  </p>
                  <div className="space-y-2.5">
                    <div className="flex items-start gap-2">
                      <TrendingUp className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                      <p className="font-['Roboto:Regular',sans-serif] text-[14px] text-gray-700 leading-relaxed">
                        High soil loads increase cleaning demands, requiring more frequent tasks and
                        shorter cleaning windows.
                      </p>
                    </div>
                    <div className="flex items-start gap-2">
                      <TrendingDown className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
                      <p className="font-['Roboto:Regular',sans-serif] text-[14px] text-gray-700 leading-relaxed">
                        Low soil loads extend time between cleanings, optimizing resource use.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="px-8 py-6 border-t border-gray-200">
                <button
                  onClick={handleSave}
                  className="w-full bg-[#61b8e7] hover:bg-[#4da3d1] text-white font-['Roboto:Bold',sans-serif] font-bold text-[16px] py-3.5 rounded-lg transition-colors"
                  type="button"
                >
                  Save and Continue
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
